// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { DocumentData } from "firebase/firestore";

import {
  UsMoOverdueRestrictiveHousingInitialHearingReferralRecord,
  usMoOverdueRestrictiveHousingInitialHearingSchema,
} from "~datatypes";

import { formatWorkflowsDate } from "../../../../utils";
import { Resident } from "../../../Resident";
import { OpportunityRequirement } from "../../types";
import {
  CopyTuple,
  CriteriaCopy,
  CriteriaFormatters,
  hydrateCriteria,
} from "../../utils";
import {
  US_MO_DAYS_PAST,
  usMoInRestrictiveHousing,
  usMoNoActiveProgressiveDisciplineSanctions,
  UsMoOverdueRestrictiveHousingBase,
} from "../UsMoOverdueRestrictiveHousingOpportunityBase/UsMoOverdueRestrictiveHousingOpportunityBase";

const usMoInitialHearingPastDueDateCopy: CopyTuple<"usMoInitialHearingPastDueDate"> =
  [
    "usMoInitialHearingPastDueDate",
    {
      text: "Initial meaningful hearing due $DAYS_PAST ($DATE)",
      tooltip:
        "If the hearing is scheduled in ITSC, the due date is the scheduled date. If NOT scheduled in ITSC, the due date is seven (7) business days after the initial assignment.",
    },
  ];

const CRITERIA_COPY: CriteriaCopy<
  UsMoOverdueRestrictiveHousingInitialHearingReferralRecord["output"]
> = {
  eligibleCriteria: [
    usMoInitialHearingPastDueDateCopy,
    usMoNoActiveProgressiveDisciplineSanctions,
    usMoInRestrictiveHousing,
  ],
  ineligibleCriteria: [usMoInitialHearingPastDueDateCopy],
};

const usMoInitialHearingPastDueDateCriteriaFormatter: NonNullable<
  CriteriaFormatters<
    UsMoOverdueRestrictiveHousingInitialHearingReferralRecord["output"]
  >["eligibleCriteria" | "ineligibleCriteria"]
>["usMoInitialHearingPastDueDate"] = {
  DAYS_PAST: ({ nextReviewDate }) => US_MO_DAYS_PAST(nextReviewDate),
  DATE: ({ nextReviewDate }) => formatWorkflowsDate(nextReviewDate),
};

const CRITERIA_FORMATTERS: CriteriaFormatters<
  UsMoOverdueRestrictiveHousingInitialHearingReferralRecord["output"]
> = {
  eligibleCriteria: {
    usMoInitialHearingPastDueDate:
      usMoInitialHearingPastDueDateCriteriaFormatter,
  },
  ineligibleCriteria: {
    usMoInitialHearingPastDueDate:
      usMoInitialHearingPastDueDateCriteriaFormatter,
  },
};

export class UsMoOverdueRestrictiveHousingInitialHearingOpportunity extends UsMoOverdueRestrictiveHousingBase<
  UsMoOverdueRestrictiveHousingInitialHearingReferralRecord["output"]
> {
  constructor(resident: Resident, record: DocumentData) {
    super(
      resident,
      "usMoOverdueRestrictiveHousingInitialHearing",
      usMoOverdueRestrictiveHousingInitialHearingSchema.parse(record),
    );
  }

  get requirementsMet(): OpportunityRequirement[] {
    return hydrateCriteria(
      this.record,
      "eligibleCriteria",
      CRITERIA_COPY,
      CRITERIA_FORMATTERS,
    );
  }

  get requirementsAlmostMet(): OpportunityRequirement[] {
    return hydrateCriteria(
      this.record,
      "ineligibleCriteria",
      CRITERIA_COPY,
      CRITERIA_FORMATTERS,
    );
  }

  get eligibilityDate(): Date | undefined {
    const initialHearingPastDueDate =
      this.record?.eligibleCriteria.usMoInitialHearingPastDueDate ||
      this.record?.ineligibleCriteria.usMoInitialHearingPastDueDate;
    return super.eligibilityDate ?? initialHearingPastDueDate?.nextReviewDate;
  }

  get eligibleStatusMessage(): string {
    return super.generateUsMoOverdueEligibilityStatusMessage(
      "Initial hearing",
      "due",
    );
  }
}
